import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { Button, DataTable, Layer, Pagination, Spinner, TextInput, Tip } from 'grommet';
import { CircleInformation, Close, SettingsOption } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { ApiError, ClientReportingPeriodService, TUpdateMultipleMeteredEquipmentUsagesRequest } from '/src/api';
import {
  Box,
  DataTableHeader,
  DataTableInputItem,
  DataTableItem,
  EquipmentUsageIcon,
  FormCard,
  InfoBox,
  Link,
  ReportingPeriodDropdown,
  Text,
} from '/src/components';
import { useEquipmentStore, useEquipmentUsageStore, useGlobalStore, useUserStore } from '/src/context';
import {
  ClientReportingPeriod,
  EquipmentType,
  EquipmentUsage,
  Facility,
  TClientId,
  TClientReportingPeriodId,
  TEquipmentId,
  TEquipmentUsageId,
  TFacilityId,
} from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { FloatString, ReportingPeriodType } from '/src/lib/types';
import { formatNumber, getPeriodFromDateString } from '/src/utils';

export type MeteredEquipmentRowData = {
  facilityId: TFacilityId;
  equipmentId: TEquipmentId;
  clientId: TClientId;
  equipmentType?: EquipmentType;
  serialNumber: string;
  manufacturer: string;
  modelYear: number;
  modelNumber: string;
  unitNumber: string;
  currentUsageId: TEquipmentUsageId;
  previousUsageId?: TEquipmentUsageId;
  previousMeteredKwh?: string;
};

export const UpdateMultipleMeteredModal: React.FC<UpdateMultipleMeteredModalProps> = observer((props) => {
  const { facility, isVisible, setIsVisible, setShouldRefresh } = props;
  const { t } = useTranslation();

  /** Stores **/
  const globalStore = useGlobalStore();
  const userStore = useUserStore();
  const equipmentStore = useEquipmentStore();
  const equipmentUsageStore = useEquipmentUsageStore();

  /** State **/
  const [isFetchingUsages, setIsFetchingUsages] = useState(true);
  const [isFetchingPeriods, setIsFetchingPeriods] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [allMeteredKwhValue, setAllMeteredKwhValue] = useState<FloatString>('');
  const [reportingPeriods, setReportingPeriods] = useState<ClientReportingPeriod[]>();
  const [selectedReportingPeriod, setSelectedReportingPeriod] = useState<ClientReportingPeriod>();
  const [currentPage, setCurrentPage] = useState(1);

  const [tableData, setTableData] = useState<MeteredEquipmentRowData[]>();
  const [formValues, setFormValues] = useState<Record<string, FloatString>>({});
  const [initialFormValues, setInitialFormValues] = useState<Record<string, FloatString>>({});

  const pageLimit = 20;

  /** Computed **/
  const isLoading = isFetchingPeriods || isFetchingUsages;
  const hasReportingPeriods = !isLoading && !!reportingPeriods?.length;
  const hasData = !isLoading && !!tableData?.length;
  const hasChanges = Object.keys(formValues).some((key) => formValues[key] !== initialFormValues[key]);
  const isCanada = facility.reporting_period_type_id === ReportingPeriodType.Yearly;
  const selectedIndex = reportingPeriods?.findIndex((p) => p.id === selectedReportingPeriod?.id) ?? -1;
  const previousPeriod = selectedIndex > -1 ? reportingPeriods?.[selectedIndex + 1] : undefined;
  const nextPeriod = selectedIndex > -1 ? reportingPeriods?.[selectedIndex - 1] : undefined;
  const totalRows = tableData?.length ?? 0;
  const paginatedData = tableData?.slice((currentPage - 1) * pageLimit, currentPage * pageLimit);

  /** Methods **/
  const getIsEditable = (period?: ClientReportingPeriod) => {
    if (!period) return false;

    const isQ2_2025 = period.start_reporting_quarter === '2025-04-01';

    if (userStore.isExternalUser) {
      return !period.is_finalized && !period.is_client_locked && !isQ2_2025;
    } else {
      return !period.is_finalized;
    }
  };

  /**
   * Set kWh Value
   */
  const setValue = (usageId: TEquipmentUsageId, newValue?: FloatString) => {
    setFormValues({
      ...formValues,
      [usageId.toString()]: newValue ?? '',
    });
  };

  const formatKwh = (value: string): FloatString => {
    const cleaned = value.replace(/[^0-9.]/g, '');
    const parts = cleaned.split('.');
    return parts.length > 2 ? `${parts[0]}.${parts.slice(1).join('')}` : cleaned;
  };

  /**
   * Set All kWh Values
   */
  const setAllValues = (newValue?: FloatString) => {
    const value = newValue ?? '';

    const newKwhValues = { ...formValues };

    for (const usageId in newKwhValues) {
      newKwhValues[usageId.toString()] = value;
    }

    setFormValues(newKwhValues);
    setAllMeteredKwhValue(value);
  };

  /**
   * Fetch Reporting Periods
   */
  const fetchReportingPeriods = async (facility: Facility) => {
    if (!facility.client_id || !facility.program_id) {
      return;
    }

    try {
      setIsFetchingPeriods(true);

      const { data } = await ClientReportingPeriodService.list({
        client_id: facility.client_id,
        program_id: facility.program_id,
        limit: 1000,
        is_finalized: 0,
        reporting_period_type_id: facility.reporting_period_type_id,
      });

      const periodData = data.reverse();

      setReportingPeriods(periodData);

      if (periodData.length) setSelectedReportingPeriod(periodData[0]);
    } catch (err) {
      globalStore.handleApiError(err as ApiError, toastMessages.listClientReportingPeriods.error);
    } finally {
      setIsFetchingPeriods(false);
    }
  };

  /**
   * Fetch EquipmentUsage Data
   */
  const fetchEquipmentUsages = async (
    facilityId: TFacilityId,
    selectedReportingPeriodId: TClientReportingPeriodId,
    previousPeriod?: ClientReportingPeriod
  ) => {
    try {
      setIsFetchingUsages(true);

      const promises = [
        equipmentUsageStore.listEquipmentUsagesByFacility({
          facility_id: facilityId,
          client_program_reporting_period_id: selectedReportingPeriodId,
          is_metered: true,
        }),
      ];
      if (previousPeriod) {
        promises.push(
          equipmentUsageStore.listEquipmentUsagesByFacility({
            facility_id: facilityId,
            client_program_reporting_period_id: previousPeriod.id,
            is_metered: true,
          })
        );
      }

      const [selectedUsages, previousUsages] = await Promise.all(promises);

      if (selectedUsages) {
        await populateTableData(selectedUsages, previousUsages);
      } else {
        console.warn('No selected usages found');
      }
    } catch (err) {
      globalStore.handleApiError(err as ApiError, toastMessages.listClientReportingPeriods.error);
    } finally {
      setIsFetchingUsages(false);
    }
  };

  /**
   * Populates Table kWh Data
   */
  const populateTableData = async (
    reportingQuarterUsages: EquipmentUsage[],
    previousReportingPeriodUsages?: EquipmentUsage[]
  ) => {
    const dataRows: MeteredEquipmentRowData[] = [];
    const formValues: Record<string, FloatString> = {};

    for (const equipmentUsage of reportingQuarterUsages) {
      formValues[equipmentUsage.id] = equipmentUsage.total_kwh ?? '';

      const equipmentType = (
        equipmentStore.equipmentCategories ||
        (await equipmentStore.fetchEquipmentCategories()) ||
        []
      )
        .flatMap((category) => ({
          name: category.name,
          equipment_types: category.equipment_types?.filter(
            (type) => type.id === equipmentUsage.equipment?.equipment_type_id
          ),
        }))
        .find((category) => !!category.equipment_types?.length);

      const dataRow: MeteredEquipmentRowData = {
        facilityId: equipmentUsage.equipment?.facility_id ?? '-',
        equipmentId: equipmentUsage.equipment?.id ?? '-',
        clientId: equipmentUsage.equipment?.client_id ?? '-',
        equipmentType: equipmentType?.equipment_types?.[0],
        serialNumber: equipmentUsage.equipment?.serial_number ?? '',
        manufacturer: equipmentUsage.equipment?.manufacturer ?? '',
        modelYear: equipmentUsage.equipment?.model_year ?? '',
        modelNumber: equipmentUsage.equipment?.model_number ?? '',
        unitNumber: equipmentUsage.equipment?.unit_number ?? '',
        currentUsageId: equipmentUsage.id,
      };

      const previousUsage = previousReportingPeriodUsages?.find(
        (usage) => usage.equipment.id === equipmentUsage.equipment.id
      );

      if (previousUsage) {
        dataRow.previousUsageId = previousUsage.id;
        dataRow.previousMeteredKwh = previousUsage.total_kwh ?? undefined;
      }

      dataRows.push(dataRow);
    }

    setTableData(dataRows);
    setFormValues(formValues);
    setInitialFormValues({ ...formValues });
    setAllMeteredKwhValue('');
    setCurrentPage(1);
  };

  /**
   * Update Metered Equipment
   */
  const updateEquipment = async () => {
    if (!facility || !selectedReportingPeriod) return;

    try {
      setIsUpdating(true);

      const equipmentUsages = Object.keys(formValues).map((usageId) => {
        const totalKwh = parseFloat(formatKwh(formValues[usageId]));
        return {
          equipment_usage_id: parseInt(usageId),
          total_kwh: Number.isFinite(totalKwh) ? totalKwh : null,
          percent_uptime: null,
          max_site_deliverability_kw: null,
        };
      });

      const updateRequest: TUpdateMultipleMeteredEquipmentUsagesRequest = {
        facility_id: facility.id,
        client_program_reporting_period_id: selectedReportingPeriod.id,
        equipment_usages: equipmentUsages,
      };

      await equipmentUsageStore.updateMultipleMeteredEquipmentUsages(updateRequest);
      toast.success(toastMessages.updateMultipleMeteredEquipment.success);
      setShouldRefresh(true);
      setIsVisible(false);
    } catch (err) {
      globalStore.handleApiError(err as ApiError, toastMessages.updateMultipleMeteredEquipment.error);
    } finally {
      setIsUpdating(false);
    }
  };

  /** Effects **/
  useEffect(() => {
    if (facility.client_id) {
      fetchReportingPeriods(facility);
    }
  }, [facility.client_id]);

  useEffect(() => {
    if (facility && selectedReportingPeriod) {
      fetchEquipmentUsages(facility.id, selectedReportingPeriod.id, previousPeriod);
    }
  }, [facility.id, selectedReportingPeriod?.id, previousPeriod?.id]);

  /** Render **/
  const renderToolbar = (reportingPeriods: ClientReportingPeriod[]) => (
    <Box row justify="between" align="center">
      <Box gap="xsmall" justify="center">
        <Box row gap="xsmall">
          <Text size="xlarge" weight={500}>
            {getIsEditable(selectedReportingPeriod) ? 'Editing' : 'Viewing'} Period:
          </Text>
          <Text size="xlarge" weight={500} color="accent-1">
            {selectedReportingPeriod &&
              getPeriodFromDateString(selectedReportingPeriod.start_reporting_quarter, isCanada)}
          </Text>
        </Box>
        <Box>
          <Text size="medium" weight={300}>
            {dayjs(selectedReportingPeriod?.start_reporting_quarter).format('MMMM DD, YYYY')} -{' '}
            {dayjs(selectedReportingPeriod?.start_reporting_quarter)
              .add(2, 'months')
              .endOf('month')
              .format('MMMM DD, YYYY')}
          </Text>
        </Box>
      </Box>
      {!getIsEditable(selectedReportingPeriod) && (
        <Box justify="center" align="center" pad="1rem" gap="xsmall">
          <Text fontFamily="Lato, sans-serif">
            {selectedReportingPeriod?.is_client_locked ||
            selectedReportingPeriod?.start_reporting_quarter === '2025-04-01'
              ? 'This period has been locked and cannot be edited.'
              : 'This period has been finalized and can no longer be edited.'}
          </Text>
        </Box>
      )}
      <ReportingPeriodDropdown
        reportingPeriods={reportingPeriods}
        setSelectedReportingPeriod={setSelectedReportingPeriod}
        selectedReportingPeriod={selectedReportingPeriod}
        previousPeriod={previousPeriod}
        nextPeriod={nextPeriod}
        isCanada={isCanada}
        showNav
      />
    </Box>
  );

  const renderEquipmentTable = () => (
    <Box elevation="small" round="xsmall" overflow="hidden">
      <DataTable
        pad={{ horizontal: 'small', vertical: 'xsmall' }}
        primaryKey="currentUsageId"
        columns={[
          {
            property: 'equipmentId',
            size: '5%',
            sortable: false,
            render: (row: MeteredEquipmentRowData) => (
              <Box pad={{ vertical: 'small' }}>
                <Link to={`/clients/${row.clientId}/equipment/${row.equipmentId}`}>
                  <Box direction="row" round="6px" justify="center" align="center">
                    <Tip content={`${t('equipment:viewEquipmentId')} ${row.equipmentId}`}>
                      <SettingsOption size="16px" color="accent-1" />
                    </Tip>
                  </Box>
                </Link>
              </Box>
            ),
          },
          {
            property: 'equipmentType.name',
            size: '16%',
            header: <DataTableHeader title={t('equipment:chargingType')} />,
            render: (row: MeteredEquipmentRowData) => <DataTableItem value={row.equipmentType?.name || '-'} />,
          },
          {
            property: 'serialNumber',
            size: '13%',
            align: 'center',
            header: <DataTableHeader title={t('equipment:serialVin')} />,
            render: (row: MeteredEquipmentRowData) => (
              <DataTableItem
                value={
                  row.serialNumber?.length >= 22
                    ? `${row.serialNumber.slice(0, 4)}...${row.serialNumber.slice(-4)}`
                    : row.serialNumber || '-'
                }
              />
            ),
          },
          {
            property: 'manufacturer',
            size: '12%',
            align: 'center',
            header: <DataTableHeader title={t('equipment:manufacturer')} />,
            render: (row: MeteredEquipmentRowData) => <DataTableItem value={row.manufacturer || '-'} />,
          },
          {
            property: 'modelYear',
            size: '10%',
            align: 'center',
            header: <DataTableHeader title={t('equipment:modelYear')} />,
            render: (row: MeteredEquipmentRowData) => <DataTableItem value={row.modelYear || '-'} />,
          },
          {
            property: 'modelNumber',
            size: '12%',
            align: 'center',
            header: <DataTableHeader title={t('equipment:modelNumber')} />,
            render: (row: MeteredEquipmentRowData) => <DataTableItem value={row.modelNumber || '-'} />,
          },
          {
            property: 'unitNumber',
            size: '10%',
            align: 'center',
            header: <DataTableHeader title={t('equipment:unitNo')} />,
            render: (row: MeteredEquipmentRowData) => <DataTableItem value={row.unitNumber || '-'} />,
          },
          {
            property: 'previousMeteredKwh',
            size: '11%',
            header: (
              <DataTableHeader>
                <Box align="center">
                  <Text size="small">{t('equipment:meteredKwh')}</Text>
                  {previousPeriod?.start_reporting_quarter && (
                    <Text size="small">
                      ({getPeriodFromDateString(previousPeriod.start_reporting_quarter, isCanada)})
                    </Text>
                  )}
                </Box>
              </DataTableHeader>
            ),
            render: (row: MeteredEquipmentRowData) =>
              row.previousMeteredKwh ? (
                <DataTableItem value={formatNumber(row.previousMeteredKwh, true)} unitName="kWh" />
              ) : (
                <DataTableItem value="-" />
              ),
          },
          {
            property: 'currentMeteredKwh',
            size: '11%',
            sortable: false,
            header: (
              <DataTableHeader>
                <Box align="center">
                  <Text size="small">{t('equipment:meteredKwh')}</Text>
                  {selectedReportingPeriod?.start_reporting_quarter && (
                    <Text size="small">
                      ({getPeriodFromDateString(selectedReportingPeriod.start_reporting_quarter, isCanada)})
                    </Text>
                  )}
                </Box>
              </DataTableHeader>
            ),
            render: (row: MeteredEquipmentRowData) =>
              getIsEditable(selectedReportingPeriod) ? (
                <Box direction="row" gap="xsmall" align="center">
                  <DataTableInputItem
                    value={formValues[row.currentUsageId.toString()] ?? ''}
                    setValue={(value) => setValue(row.currentUsageId, formatKwh(value.toString()))}
                  />
                  <Text size="small">kWh</Text>
                </Box>
              ) : (
                <DataTableItem value={formatNumber(formValues[row.currentUsageId.toString()], true)} unitName="kWh" />
              ),
          },
        ]}
        data={paginatedData}
        sortable
        background={['light-6', 'white']}
        border={{ color: 'light-2', side: 'bottom', size: 'small' }}
      />
    </Box>
  );

  const renderBulkEditField = () => (
    <InfoBox direction="row" justify="between" align="center" gap="medium">
      <Box direction="row" gap="small" align="center">
        <CircleInformation size="medium" color="accent-1" />
        <Box gap="xxsmall">
          <Text weight={700}>
            {t('equipment:bulkEditAllKwh')}
            {selectedReportingPeriod?.start_reporting_quarter &&
              ` (${getPeriodFromDateString(selectedReportingPeriod.start_reporting_quarter, isCanada)})`}
          </Text>
          <Text size="small" color="dark-4">
            {t('equipment:bulkEditAllKwhHelp')}
          </Text>
        </Box>
      </Box>
      <Box direction="row" gap="xsmall" align="center" width="20%">
        <TextInput
          name="all_metered_kwh_value"
          value={allMeteredKwhValue}
          type="number"
          onChange={(e) => setAllValues(e.target.value)}
          style={{ background: 'white', paddingTop: '8px', paddingBottom: '8px' }}
          className="hide-input-arrows"
          disabled={!getIsEditable(selectedReportingPeriod)}
        />
        <Text size="small">kWh</Text>
      </Box>
    </InfoBox>
  );

  const renderDataTable = () => (
    <Box gap="medium">
      {getIsEditable(selectedReportingPeriod) && renderBulkEditField()}
      {renderEquipmentTable()}
      {totalRows > pageLimit && (
        <Box justify="center" align="center">
          <Pagination
            alignSelf="center"
            size="small"
            page={currentPage}
            step={pageLimit}
            numberItems={totalRows}
            onChange={(e) => setCurrentPage(e.page)}
          />
        </Box>
      )}
    </Box>
  );

  return isVisible ? (
    <Layer
      position="center"
      onClickOutside={() => setIsVisible(false)}
      onEsc={() => setIsVisible(false)}
      responsive={false}
      style={{ width: '90%', maxWidth: '1400px', maxHeight: '90vh', overflow: 'auto' }}
      margin="none"
    >
      <FormCard
        width="100%"
        title={t('equipment:updateMeteredEquipmentUsage')}
        icon={<EquipmentUsageIcon />}
        onSubmit={() => updateEquipment()}
        isLoading={isUpdating}
        hideRequiredText
        hideSaveButton={!hasReportingPeriods || !getIsEditable(selectedReportingPeriod)}
        saveButtonLabel={t('common:saveChanges')}
        saveButtonLoadingLabel={t('common:savingChanges')}
        saveButtonDisabled={!hasData || !hasChanges}
        headerChildren={[
          <Button
            key="close"
            pad="none"
            onClick={() => setIsVisible(false)}
            icon={<Close size="20px" />}
            tip={t('common:close')}
          />,
        ]}
      >
        <Box gap="medium">
          {hasReportingPeriods && renderToolbar(reportingPeriods)}
          {isLoading && (
            <Box pad={{ vertical: 'medium' }} height="10vh" fill="horizontal" justify="center" align="center">
              <Spinner size="medium" />
            </Box>
          )}
          {!isLoading && (
            <>
              {hasData ? (
                renderDataTable()
              ) : (
                <Box pad={{ vertical: '2rem' }} background="light-6" align="center">
                  <Text size="medium" fontFamily="Lato, sans-serif">
                    {hasReportingPeriods
                      ? t('equipment:noMeteredEquipmentUsageDataFound')
                      : t('equipment:noUsageDataFound')}
                  </Text>
                </Box>
              )}
            </>
          )}
        </Box>
      </FormCard>
    </Layer>
  ) : (
    <></>
  );
});

export type UpdateMultipleMeteredModalProps = {
  facility: Facility;
  isVisible?: boolean;
  setIsVisible: (isVisible: boolean) => void;
  setShouldRefresh: (shouldRefresh: boolean) => void;
};
